import { T_Coupon, T_CTCartResponse, T_Order, T_OrderProduct, T_OrderTotal } from '../types';
import { couponStoreApi } from '../store/couponStore';
import { skuRatings } from '../config';

/**
 * Maps a raw CommerceTools /createCart or /updateCart response into the
 * shared T_Order shape that the rest of the app consumes.
 * Also applies any campaigns from the CT response to the coupon store.
 */
export const mapCampaignsToCoupons = (campaigns: any[]): T_Coupon[] => {
  if (!campaigns?.length) return [];
  return campaigns.map(c => ({
    couponCode: c.campaign ?? '',
    name: c.name ?? '',
    description: c.description ?? '',
    id: c.id ?? '',
    custom1: c.custom1 ?? '',
    custom2: c.custom2 ?? '',
    custom3: c.custom3 ?? '',
    custom4: c.custom4 ?? '',
    custom5: c.custom5 ?? '',
  }));
};

export function mapCTCartToOrder(ct: T_CTCartResponse): T_Order {
  const items = ct?.items ?? [];
  // Group items by category first
  const groupedItems = items.reduce<Record<string, typeof items>>((acc, item) => {
    if (!acc[item.categoryId]) {
      acc[item.categoryId] = [];
    }

    acc[item.categoryId].push(item);

    return acc;
  }, {});

  // Flatten category-wise ordered items
  const sortedItems = Object.values(groupedItems).flat();

  const orderProducts: T_OrderProduct[] = sortedItems.map(item => {
    const rating = item?.skuRating ?? skuRatings[item.code] ?? 0; // Remove Rating Option
    return {
      productId: item.productId,
      quantity: item.quantity,
      price: item.discountPrice,
      finalPriceIncTax: item.price,
      categoryId: item.categoryId,
      categoryName: item.category,
      promotionTitle: '',
      rating,
      opts: null,
      currentlySubscribed: item.type === 'EXISTING',
      occurrenceNumber: item.occurrenceNumber ?? undefined,
      product: {
        id: item.productId,
        sku: item.code,
        name: item.productName,
        price0: item.price,
        price1: item.price1,
        price3: 0,
        priceIncTax: item.price,
        categoryId: item.categoryId,
        categoryName: item.category,
        quantityInCart: item.quantity,
        quantityBought: 0,
        rating: rating,
        promotionTitle: '',
        accessories: [],
        prodRelationTypeArray: [],
        channels: [],
        url: '',
        idInCart: 0,
        description: '',
        detailDescription: {} as JSON,
        comparison: '',
        image: '',
        custom1: item.custom1 ?? '',
        custom2: item.custom2 ?? '',
        custom3: item.custom3 ?? '',
        custom4: item.custom4 ?? '',
        custom5: item.custom5 ?? '',
        custom6: item.custom6 ?? '',
        custom7: item.custom7 ?? '',
        custom8: item.custom8 ?? '',
        custom9: item.custom9 ?? '',
        custom10: item.custom10 ?? '',
      },
    };
  });
  const mappedCoupons = mapCampaignsToCoupons(ct?.campaigns);
  couponStoreApi.getState().applyMultipleCTCouponsToStore(mappedCoupons);
  const orderTotals: T_OrderTotal[] = [
    {
      className: 'ot_total',
      value: ct?.cartTotalPrice,
      title: 'Total',
      freeProductId: 0,
      id: 0,
      orderId: 0,
      promotionId: 0,
      promotions: [],
    },
    {
      className: 'ot_total_no_otc',
      value: ct?.recurringTotalPrice,
      title: 'Total with promotion but no one off fees',
      freeProductId: 0,
      id: 0,
      orderId: 0,
      promotionId: 0,
      promotions: [],
    },
    {
      className: 'ot_subtotal',
      value: ct?.ongoingTotalPrice,
      title: 'Sub-Total',
      freeProductId: 0,
      id: 0,
      orderId: 0,
      promotionId: 0,
      promotions: [],
    },
    {
      className: 'ot_technician_fee',
      value: ct?.otcTechnicianFee,
      title: 'Technician Fee',
      freeProductId: 0,
      id: 0,
      orderId: 0,
      promotionId: 0,
      promotions: [],
    },
    {
      className: 'ot_total_data',
      value: ct?.otcTotalData,
      title: 'Total Data',
      freeProductId: 0,
      id: 0,
      orderId: 0,
      promotionId: 0,
      promotions: [],
    },
  ];

  return {
    customerId: ct?.customerId,
    orderProducts,
    orderTotals,
  };
}
